// ============================================================================
// Canonical Layer Batch record route contract
// ============================================================================
//
// `{view: 'layerbatches', paths: ['/layer/batches/*', '/layer/housings/*']}` is
// a TWO-PATH row, and LayerBatchesRouter picks between three shapes on the RAW
// pathname alone:
//
//   /layer/housings/<id>  -> layer.housings.detail  (LayerHousingPage, released)
//   /layer/batches/<id>   -> layer.batches.detail   (LayerBatchPage)
//   /layer/batches/       -> layer.batches.detail   (empty id, "not found")
//   anything else         -> layer.batches.index    (LayerBatchesHub, released)
//
// This module claims the SECOND of those, and only with exactly one
// decoded-safe segment. The trailing-slash base is deliberately excluded: the
// router really does mount LayerBatchPage there — `batchId` is the empty
// string, so the page paints its own "Batch not found." — but that is not the
// direct-entry route any reviewer audited, and `surfaceForRoutedView`
// classifies it as this same surface. An unfenced RELEASED verdict would
// suppress recovery, so admitting it would strand the frame instead of merely
// rendering a narrower page.
//
// WHY THIS IS A SEPARATE MODULE FROM ITS HOUSINGS TWIN. The two predicates are
// structurally alike and differ only in their prefix, but they are two
// SEPARATELY AUDITED release contracts under one router, and each is pinned
// character-for-character by its own release proof. Folding them into one
// parameterised helper would mean a future widening of either contract silently
// widened the other — which is the specific failure the per-surface fences in
// `authenticatedSurfaceResolver` exist to prevent. Every released record route
// in this directory is its own module for the same reason.
//
// This module knows nothing about the ledger, the renderer, or any data owner.
// Its only job is to keep the reviewed shape from drifting as wide as the
// historical router, which accepts every `/layer/batches/` descendant. The
// released boundary keeps wider raw shapes in explicit recovery.
//
// RESERVED SEGMENTS: there are NONE beneath `/layer/batches/`.
// `src/lib/routes.ts` registers `/layer/batches` as the view's exact canonical
// `VIEW_TO_PATH.layerbatches` and nothing deeper, and the layer domain appears
// in neither ALIASES_EXACT nor ALIASES_PREFIX. LAYER_ROUTES declares no sibling
// namespace inside the batches splat — unlike `/cattle/batches/forecast`, which
// `surfaceForRoutedView` really does special-case, or TasksRouter's `todo` — and
// the router's own branch is a bare `startsWith` with the page slicing the
// remainder, so no value is special-cased. That absence is DERIVED in the
// release proof, not asserted here: a future `/layer/batches/archive` row would
// have to add a reserved set to this module AND appear in that proof; nothing
// else would notice it.
//
// The bare `/layer/batches` is NOT this surface and is not merely excluded here
// — it is the released `layer.batches.index` hub, whose own target is an
// EQUALITY against that exact pathname. The two are disjoint by construction:
// this predicate requires the strictly longer `/layer/batches/` prefix plus a
// non-empty segment, so no pathname can satisfy both.
// ============================================================================

import {isCanonicalCommentAnchorHash} from './commentAnchorHash.ts';

export const LAYER_BATCHES_DETAIL_PATHNAME_PREFIX = '/layer/batches/';

type LocationLike = Readonly<{pathname?: unknown; search?: unknown; hash?: unknown}>;

// One meaningful raw segment: no separator in either direction, no NUL, no line
// break, and no query/fragment marker smuggled into a bare pathname string.
function isSafeRawSegment(rawSegment: string): boolean {
  return !(
    rawSegment === '' ||
    rawSegment.includes('/') ||
    rawSegment.includes('\\') ||
    rawSegment.includes('\0') ||
    rawSegment.includes('\r') ||
    rawSegment.includes('\n') ||
    rawSegment.includes('?') ||
    rawSegment.includes('#')
  );
}

// The same rules again after ONE decode, which is what turns `%2F`, `%5C`,
// `%00`, `%0A`, `%0D`, `%2E`, and `%2E%2E` into rejections rather than into
// accepted opaque text. A malformed escape makes decodeURIComponent throw and
// the caller's catch returns false.
function isSafeDecodedSegment(segment: string): boolean {
  return (
    segment.trim() !== '' &&
    segment !== '.' &&
    segment !== '..' &&
    !segment.includes('/') &&
    !segment.includes('\\') &&
    !segment.includes('\0') &&
    !segment.includes('\r') &&
    !segment.includes('\n') &&
    !segment.includes('?') &&
    !segment.includes('#')
  );
}

/**
 * `/layer/batches/<id>` with exactly one decoded-safe segment.
 *
 * `/layer/batches` does not start with the prefix and `/layer/batches/` ends at
 * it, so this predicate claims neither. The raw segment is checked BEFORE the
 * decoder so a literal separator, NUL, or line break never reaches it, then the
 * value is decoded once and re-checked so an encoded separator, dot segment,
 * NUL, or line break cannot widen the record release.
 */
export function isCanonicalLayerBatchesDetailPathname(pathname: unknown): pathname is string {
  if (typeof pathname !== 'string' || !pathname.startsWith(LAYER_BATCHES_DETAIL_PATHNAME_PREFIX)) {
    return false;
  }
  const rawSegment = pathname.slice(LAYER_BATCHES_DETAIL_PATHNAME_PREFIX.length);
  if (!isSafeRawSegment(rawSegment)) return false;
  try {
    return isSafeDecodedSegment(decodeURIComponent(rawSegment));
  } catch {
    return false;
  }
}

/**
 * The record page READS `location.hash`: it scrolls a deep-linked
 * `#comment-<id>` mention from RecordCollaborationSection into view. A
 * hash-carrying load is therefore a materially different render than the
 * audited one, so both search and hash stay outside the release and receive
 * explicit route recovery. This is the same line its released housings sibling
 * draws, for the same reason and from the same effect.
 *
 * `location.state` is deliberately NOT part of this contract. The page reads
 * `state.recordSeq` for its prev/next controls, but route state is absent on
 * every direct entry and the page already treats that absence as "hide the
 * controls" — so a state-bearing load is a NARROWER render of the audited one,
 * not a different one. Its released housings sibling reads the same key from
 * the same helper and draws the same line.
 *
 * A bare string is accepted only when it is plainly a pathname, so a caller or
 * test seam cannot smuggle an unsupported query or fragment into the target.
 */
export function isCanonicalLayerBatchesDetailLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalLayerBatchesDetailPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;

  const {pathname, search, hash} = location as LocationLike;
  return (
    (search === undefined || search === '') &&
    (hash === undefined || hash === '' || isCanonicalCommentAnchorHash(hash)) &&
    isCanonicalLayerBatchesDetailPathname(pathname)
  );
}
